/**
 * * 新增 `dvBoxLayout` 配置项来配置网格布局
 *
 * - 实现根据 Y 轴刻度标签文本位置和宽度可以自动自适应计算 grid.left 和 grid.right 的值
 * - 仅对 Y 轴有效
 * - 支持固定像素距离留白配置
 * - 支持动画过渡，动画取决于全局的更新动画配置
 *
 * @example
 * ```javascript
  {
    dvBoxLayout: {
      enable: true,
      padding: [8, 8],
    }
  }
 * ```
 */
import type { EChartsExtensionInstallRegisters } from 'echarts/lib/extension';
import type ExtensionAPI from 'echarts/lib/core/ExtensionAPI';
import type GlobalModel from 'echarts/lib/model/Global';
import type AxisModel from 'echarts/lib/coord/cartesian/AxisModel';
import { getInstanceById } from 'echarts/core';
import { getAnimationConfig } from 'echarts/lib/animation/basicTrasition';
import type { GridModel } from './type';
import { getStandardChart } from '../../../core/StandardChart';

const TIMERS_KEY = 'extension_dvboxlayout_timers';
const RERENDER_FLAG_KEY = '__extension_dvboxlayout_rerender_flag';
const FRAME_TIME = 16;

function resetAndCheckEnable(ecModel: GlobalModel) {
  let enable = false;

  // * reset
  ecModel.eachComponent('grid', (gridModel: GridModel) => {
    // @ts-ignore
    enable = !!gridModel.get('dvBoxLayout.enable');

    gridModel.__$$cacheDvBoxLayout = { leftValues: [], rightValues: [] };
  });

  return enable;
}

function computeLayout(ecModel: GlobalModel, api: ExtensionAPI) {
  /** 同一个 grid 上可能对应多个 y 轴，用 Map 应对多 Grid 场景 */
  const firstYAxisAtLeftMap = new WeakMap<GridModel, boolean>();

  ecModel.eachComponent('yAxis', (axis: AxisModel, index: number) => {
    const grid = axis.getCoordSysModel() as GridModel;

    if (
      // @ts-ignore
      !grid.get('dvBoxLayout.enable') ||
      !axis.get('show') ||
      // @ts-ignore
      !axis.get('axisLabel.show') ||
      // @ts-ignore
      axis.get('axisLabel.inside') === true
    ) {
      return;
    }

    let atLeft = axis.axis.position !== 'right';
    const gridRect = grid.coordinateSystem.getRect();
    const axisView = api.getViewOfComponentModel(axis);
    const axisViewRect = axisView.group.getBoundingRect().clone();
    axisViewRect.applyTransform(axisView.group.transform);

    if (!firstYAxisAtLeftMap.has(grid)) {
      firstYAxisAtLeftMap.set(grid, atLeft);
    } else {
      // @see https://echarts.apache.org/zh/option.html#yAxis.position
      atLeft = !firstYAxisAtLeftMap.get(grid);
    }

    const diffX = atLeft
      ? gridRect.x - axisViewRect.x
      : axisViewRect.x + axisViewRect.width - (gridRect.x + gridRect.width);
    const padding = ((grid.get(
      // @ts-ignore
      'dvBoxLayout.padding'
    ) as GridModel['option']['dvBoxLayout']['padding']) || [0, 0])[atLeft ? 0 : 1];

    (axis.getCoordSysModel() as GridModel).__$$cacheDvBoxLayout[
      atLeft ? 'leftValues' : 'rightValues'
    ].push(Math.max(diffX + 1 + padding, 0));
  });
}

function updateAndCheckNeedRerender(ecModel: GlobalModel) {
  let needRerender = false;

  ecModel.eachComponent('grid', (gridModel: GridModel) => {
    const leftValues = gridModel.__$$cacheDvBoxLayout.leftValues || [];
    const rightValues = gridModel.__$$cacheDvBoxLayout.rightValues || [];

    if (leftValues.length > 0) {
      gridModel.option.left = Math.max(...leftValues);
      needRerender = true;
    }
    if (rightValues.length > 0) {
      gridModel.option.right = Math.max(...rightValues);
      // eslint-disable-next-line thsjs/no-redundant-assignments
      needRerender = true;
    }
  });

  return needRerender;
}

function triggerRerender(
  ecModel: GlobalModel,
  ecIns: ReturnType<typeof getInstanceById>,
  standardChartIns: ReturnType<typeof getStandardChart>
) {
  standardChartIns.cacheTimers(TIMERS_KEY, [
    setTimeout(() => {
      // * trigger render
      ecIns.resize({
        animation: ecModel.isAnimationEnabled() ? getAnimationConfig('update', ecModel, 0) : null,
        silent: true
      });
    }, FRAME_TIME)
  ]);

  standardChartIns.getTimers(TIMERS_KEY)[RERENDER_FLAG_KEY] = true;
}

export function install(registers: EChartsExtensionInstallRegisters) {
  registers.registerUpdateLifecycle('afterupdate', (ecModel, api) => {
    const ecIns = getInstanceById(api.getId());
    const standardChartIns = getStandardChart(ecIns);
    const rerenderFlag = standardChartIns.getTimers(TIMERS_KEY)[RERENDER_FLAG_KEY] as boolean;

    if (rerenderFlag) {
      standardChartIns.getTimers(TIMERS_KEY)[RERENDER_FLAG_KEY] = false;

      return;
    }

    const enable = resetAndCheckEnable(ecModel);
    if (!enable) {
      return;
    }

    // * compute
    computeLayout(ecModel, api);

    // * update
    const needRerender = updateAndCheckNeedRerender(ecModel);

    if (needRerender) {
      triggerRerender(ecModel, ecIns, standardChartIns);
    }
  });
}
